import {isFeatureEnabled} from 'feature-flags';
import {Action, TabProps, Tabs} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {always} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  AdvertisementStatusEnum,
  GetSalesPlatformsApiRes,
  SalesPlatformsResBody,
  useGetGeneralSettingsQuery,
  useGetSalesPlatformsQuery,
  useGetVehicleQuery,
  useRenderWindshieldPaperDocumentMutation,
  useUpdateVehicleIsInSaleMutation,
  useUpdateVehicleOnAllPlatformsMutation,
} from '@dms/api/sales';
import {useValidateMagicPublishingMutation} from '@dms/api/settings';
import {ApiError, EntityResourceIds} from '@dms/api/shared';
import {ConditionEnum} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  handleApiError,
  hasAclDocsWithPermission,
  printPDF,
  queryParams,
  useDocumentTemplates,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
  usePermissions,
} from '@dms/shared';

import {buildArray, openInNewTab, useQueryState, useRequiredParams} from 'shared/web';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';
import {openMagicPublishingDialog} from '../utils/openMagicPublishingDialog';
import {AdvertisementTab} from './AdvertisementTab';
import {WindshieldTab} from './WindshieldTab';

const TAB_IDS = {
  AD_PLATFORMS: 'adPlatforms',
  WINDSHIELD: 'windshield',
};

export function AdvertisementContent() {
  const {id: vehicleId} = useRequiredParams();
  const {resetPollingInterval, isMagicPublishingActive, setActiveMagicRequestId} =
    useAdvertisementPolling();
  const {isMandatoryFieldsEnabled} = useMandatoryFields();
  const {handleMandatoryFieldsError, isMandatoryFieldsError} =
    useMandatoryFieldsErrorDialog(vehicleId);
  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId});

  const [
    hasGenerateWindshieldPaperPermission,
    hasUpdateVehicleOnAllPlatformPermission,
    hasEnableVehicleInAdvertisementPermission,
    hasDisableVehicleInAdvertisementPermission,
    hasReadWindsheildPaperDocumentPermission,
  ] = usePermissions({
    permissionKeys: [
      'generateWindshieldPaper',
      'updateVehicleOnAllPlatform',
      'enableVehicleInAdvertisement',
      'disableVehicleInAdvertisement',
      'readWindsheildPaperDocument',
    ],
    scopes: {
      generateWindshieldPaper: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      updateVehicleOnAllPlatform: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      enableVehicleInAdvertisement: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      disableVehicleInAdvertisement: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      readWindsheildPaperDocument: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const [tabId, setActiveTabId] = useQueryState(queryParams.VEHICLE_ADVERTISEMENT_PUBLISHING);
  const activeTabId = tabId ?? TAB_IDS.AD_PLATFORMS;

  const [updateVehicleOnAllPlatforms] = useUpdateVehicleOnAllPlatformsMutation();
  const [updateVehicleIsInSale, {isLoading: isUpdatingVehicleIsInSale}] =
    useUpdateVehicleIsInSaleMutation();
  const [validateMagicPublishing, {isLoading: isValidatingMagicPublishing}] =
    useValidateMagicPublishingMutation();
  const [renderWindshieldPaperDocument] = useRenderWindshieldPaperDocumentMutation();
  const {data: platforms} = useGetSalesPlatformsQuery({vehicleId});

  const handleRequestCreated = (requestId: string) => {
    setActiveMagicRequestId(requestId);
  };

  const [getTemplatesByCode] = useDocumentTemplates();
  const {getDropdownComponent} = getTemplatesByCode('windshield-paper');

  const {data: generalSettings} = useGetGeneralSettingsQuery({vehicleId});
  const inAdvertisement = generalSettings?.inSale;

  const platformsPublishedCount = getPublishedPlatformsLength(platforms);
  const possiblePlatformUpdateCount = getPlatformsToUpdateLength(platforms);

  const downloadTemplate = (templateId: string) => {
    renderWindshieldPaperDocument({
      windshieldPaperRequestBody: {
        templateId,
        vehicleId,
      },
    })
      .unwrap()
      .then((result) => {
        if (result?.url) {
          openInNewTab(result.url);
        }
      })
      .catch(handleApiError);
  };

  const printTemplate = (templateId: string) => {
    renderWindshieldPaperDocument({
      windshieldPaperRequestBody: {
        templateId,
        vehicleId,
      },
    })
      .unwrap()
      .then((result) => {
        if (result?.pdfUrl) {
          printPDF(result.pdfUrl);
        }
      })
      .catch(handleApiError);
  };

  const isLoading = getIsAnyPlatformProcessing(platforms);

  const isUpdateShown = match([
    inAdvertisement,
    possiblePlatformUpdateCount,
    platformsPublishedCount,
  ])
    .with(
      [false, Pattern.any, Pattern.any],
      [Pattern.any, 0, Pattern.any],
      [Pattern.any, Pattern.any, 0],
      always(false)
    )
    .otherwise(always(true));

  const handleUpdate = () => {
    resetPollingInterval();
    updateVehicleOnAllPlatforms({vehicleId}).unwrap().catch(handleApiError);
  };

  const handlePublish = async () => {
    resetPollingInterval();
    await updateVehicleIsInSale({
      vehicleId,
      publish: !inAdvertisement,
      validateMandatoryFields: isMandatoryFieldsEnabled(
        (vehicle?.vehicle?.condition as ConditionEnum) ?? undefined
      ),
    })
      .unwrap()
      .catch((error: ApiError) => {
        if (isMandatoryFieldsError(error)) {
          return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
        }

        return handleApiError(error);
      });
  };

  const handleMagicPublishing = () => {
    validateMagicPublishing({vehicleId})
      .unwrap()
      .then((result) => {
        openMagicPublishingDialog({
          vehicleId,
          missingFields: result.missingFields,
          missingMandatoryFields: result.missingMandatoryFields,
          hasSalesPhotos: result.hasSalesPhotos,
          onRequestCreated: handleRequestCreated,
          'data-testid': testIds.vehicles.publishing('magicPublishingDialog'),
        });
      })
      .catch(handleApiError);
  };

  const platformActions = buildArray<Action>()
    .when(isFeatureEnabled(featureFlags.MAGIC_BUTTON) && !inAdvertisement, {
      title: i18n.t('entity.vehicle.labels.magicPublishing'),
      onClick: handleMagicPublishing,
      isLoading: isValidatingMagicPublishing || isMagicPublishingActive,
      type: 'button',
      variant: 'outlined',
      leftIcon: 'action/magic_wand',
      'data-testid': testIds.vehicles.publishing('magicButton'),
    })
    .when(hasUpdateVehicleOnAllPlatformPermission && isUpdateShown, {
      title: i18n.t('page.advertisement.actions.updateAd'),
      onClick: handleUpdate,
      isLoading,
      type: 'button',
      variant: 'outlined',
      'data-testid': testIds.vehicles.publishing('refresh'),
    })
    .when(hasDisableVehicleInAdvertisementPermission && inAdvertisement, {
      title: i18n.t('page.advertisement.actions.unPublish'),
      onClick: handlePublish,
      isLoading: isLoading || isUpdatingVehicleIsInSale,
      type: 'button',
      variant: 'danger',
      'data-testid': testIds.vehicles.publishing('publish'),
    })
    .when(hasEnableVehicleInAdvertisementPermission && !inAdvertisement, {
      title: i18n.t('page.advertisement.actions.publish'),
      onClick: handlePublish,
      isLoading: isLoading || isUpdatingVehicleIsInSale,
      type: 'button',
      variant: 'primary',
      'data-testid': testIds.vehicles.publishing('publish'),
    });

  const tabs: TabProps[] = [
    {
      id: TAB_IDS.AD_PLATFORMS,
      'data-testid': testIds.vehicles.publishing('adPlatforms'),
      title: i18n.t('entity.vehicle.labels.advertisementTabPlatforms'),
      content: (
        <AdvertisementTab
          isPollingEnabled={activeTabId === TAB_IDS.AD_PLATFORMS}
          data-testid={testIds.vehicles.publishing('adPlatformsContent')}
        />
      ),
      actions: platformActions,
    },
    {
      id: TAB_IDS.WINDSHIELD,
      'data-testid': testIds.vehicles.publishing('windshield'),
      title: i18n.t('entity.vehicle.labels.windshieldTab'),
      content: <WindshieldTab data-testid={testIds.vehicles.publishing('windshieldContent')} />,
      actions: buildArray<Action>()
        .when(hasGenerateWindshieldPaperPermission, {
          type: 'custom',
          component: getDropdownComponent({
            title: i18n.t('page.advertisement.labels.downloadWindshield'),
            isDisabled: !hasAclDocsWithPermission(hasReadWindsheildPaperDocumentPermission),
            onChange: downloadTemplate,
          }),
        })
        .when(hasGenerateWindshieldPaperPermission, {
          type: 'custom',
          component: getDropdownComponent({
            title: i18n.t('page.advertisement.labels.printWindshield'),
            isDisabled: !hasAclDocsWithPermission(hasReadWindsheildPaperDocumentPermission),
            onChange: printTemplate,
          }),
        }),
    },
  ];

  return (
    <Tabs
      variant="condensed"
      onChange={setActiveTabId}
      activeTabId={activeTabId}
      tabs={tabs}
      data-testid={testIds.vehicles.publishing('tabs')}
    />
  );
}

const getIsAnyPlatformProcessing = (platforms: GetSalesPlatformsApiRes | undefined): boolean => {
  if (!platforms) {
    return false;
  }

  return platforms.some((value) =>
    match(value.status)
      .with(AdvertisementStatusEnum.PUBLISHING, AdvertisementStatusEnum.UNPUBLISHING, always(true))
      .otherwise(always(false))
  );
};

const getPublishedPlatformsLength = (platforms: GetSalesPlatformsApiRes | undefined) =>
  platforms?.reduce(
    (previous: number, current: SalesPlatformsResBody) =>
      current.status === AdvertisementStatusEnum.PUBLISHED ? previous + 1 : previous,
    0
  ) ?? 0;

const getPlatformsToUpdateLength = (platforms: GetSalesPlatformsApiRes | undefined) =>
  platforms?.reduce(
    (previous: number, current: SalesPlatformsResBody) =>
      (current.status === AdvertisementStatusEnum.PUBLISHED ||
        current.status === AdvertisementStatusEnum.UPDATE_FAILED) &&
      current.platform.enabled
        ? previous + 1
        : previous,
    0
  ) ?? 0;
